import { Order } from '@/lib/constants';
import { formatCurrency, formatTime, formatOrderId } from '@/lib/format';
import { Clock, MapPin, Package, User, MessageSquare } from 'lucide-react';

interface OrderCardProps {
  order: Order;
  onAccept: (orderId: string) => void;
  onReject: (orderId: string) => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
}

export default function OrderCard({
  order,
  onAccept,
  onReject,
  isAccepting = false,
  isRejecting = false,
}: OrderCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'accepted':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'preparing':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'ready_for_pickup':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const isPending = order.status === 'pending';

  return (
    <div className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            {formatOrderId(order.id)}
          </h3>
          <p className="text-sm text-muted-foreground">
            {formatTime(order.createdAt)}
          </p>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
            order.status
          )}`}
        >
          {order.status.replace('_', ' ').toUpperCase()}
        </span>
      </div>

      {/* Customer Info */}
      <div className="flex items-center gap-2 mb-3">
        <User className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-foreground">
          {order.customerName || 'Customer #' + (order.customerId?.slice(0, 8) || 'N/A')}
        </span>
      </div>

      {/* Delivery Address */}
      <div className="flex items-start gap-2 mb-3">
        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-foreground">
            {order.deliveryAddress || 'No address provided'}
          </p>
        </div>
      </div>

      {/* Items */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Package className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {order.items?.length || 0} item(s)
          </span>
        </div>
        {order.items && order.items.length > 0 && (
          <div className="ml-6 space-y-1">
            {order.items.map((item, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-foreground">
                  {item.name || 'Item'}{' '}
                  {(item as any).weightKg ? (
                    <span className="text-muted-foreground">
                      ({item.quantity > 0 ? item.quantity.toFixed(3).replace(/\.?0+$/, '') : item.quantity} kg)
                    </span>
                  ) : (
                    <span className="text-muted-foreground">×{item.quantity}</span>
                  )}
                </span>
                <span className="text-muted-foreground">
                  {formatCurrency(item.price * item.quantity)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Buyer Notes (Sprint 27) */}
      {order.notes && (
        <div className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-700 mb-1">Customer Notes</p>
              <p className="text-sm text-blue-800">{order.notes}</p>
            </div>
          </div>
        </div>
      )}

      {/* Total */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-muted-foreground">Total</span>
          <span className="text-xl font-bold text-foreground">
            {formatCurrency(order.totalAmount)}
          </span>
        </div>
      </div>

      {/* SLA Timer (for pending orders) */}
      {isPending && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
          <Clock className="w-4 h-4 text-yellow-700" />
          <span className="text-sm text-yellow-700 font-medium">
            Auto-cancel in: 5 minutes
          </span>
        </div>
      )}

      {/* Actions */}
      {isPending && (
        <div className="flex gap-3">
          <button
            onClick={() => onReject(order.id)}
            disabled={isAccepting || isRejecting}
            className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRejecting ? 'Rejecting...' : 'Reject'}
          </button>
          <button
            onClick={() => onAccept(order.id)}
            disabled={isAccepting || isRejecting}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAccepting ? 'Accepting...' : 'Accept Order'}
          </button>
        </div>
      )}
    </div>
  );
}

